import { useState, useEffect } from 'react';
import { useModalA11y } from '../hooks/use-modal-a11y';

interface QuantityModificationModalProps {
  isOpen: boolean;
  onClose: () => void;
  onSubmit: (approvedQuantity: number, reason: string) => void;
  lineItemName?: string;
  originalRequestedQuantity?: number; // The original quantity from the order request
  currentApprovedQuantity?: number; // The most recent approved quantity (from previous level)
  unitOfMeasure?: string;
  allowIncrease?: boolean; // When false, the approver may only decrease the quantity, never increase it
  isSubmitting?: boolean;
}

export function QuantityModificationModal({ 
  isOpen, 
  onClose, 
  onSubmit, 
  lineItemName,
  originalRequestedQuantity = 0,
  currentApprovedQuantity,
  unitOfMeasure = 'units',
  allowIncrease = true,
  isSubmitting = false 
}: QuantityModificationModalProps) {
  // Use currentApprovedQuantity if it exists, otherwise use originalRequestedQuantity
  const effectiveQuantity = currentApprovedQuantity ?? originalRequestedQuantity;
  
  const [approvedQuantity, setApprovedQuantity] = useState<string>(effectiveQuantity.toString());
  const [reason, setReason] = useState('');
  const [quantityError, setQuantityError] = useState('');
  const [reasonError, setReasonError] = useState('');

  // Reset form when modal opens/closes
  useEffect(() => {
    if (isOpen) {
      setApprovedQuantity(effectiveQuantity.toString());
      setReason('');
      setQuantityError('');
      setReasonError('');
    }
  }, [isOpen, effectiveQuantity]);

  const handleSubmit = (e: React.FormEvent) => {
    e.preventDefault();
    
    console.log('QuantityModificationModal: handleSubmit called', {
      approvedQuantity,
      reason,
      effectiveQuantity,
      lineItemName
    });
    
    // Clear previous errors
    setQuantityError('');
    setReasonError('');
    
    // Validate quantity
    const parsedQuantity = parseFloat(approvedQuantity);
    if (!approvedQuantity || isNaN(parsedQuantity) || parsedQuantity < 1) {
      console.log('QuantityModificationModal: Validation failed - invalid quantity');
      setQuantityError('Quantity must be at least 1. Please reject the item if you do not want to approve it.');
      return;
    }

    // If this step doesn't allow increasing the quantity, reject values above the effective quantity
    if (!allowIncrease && parsedQuantity > effectiveQuantity) {
      console.log('QuantityModificationModal: Validation failed - increase not allowed at this step');
      setQuantityError(`This step only allows decreasing the quantity. Maximum allowed is ${effectiveQuantity.toLocaleString()} ${unitOfMeasure}.`);
      return;
    }

    // If quantity was changed from the current effective quantity, require a reason
    if (parsedQuantity !== effectiveQuantity) {
      if (!reason.trim()) {
        console.log('QuantityModificationModal: Validation failed - no reason provided for quantity change');
        setReasonError('Please provide a reason for changing the quantity');
        return;
      }
    }

    console.log('QuantityModificationModal: Validation passed, calling onSubmit', {
      parsedQuantity,
      trimmedReason: reason.trim()
    });

    // Submit the modification
    onSubmit(parsedQuantity, reason.trim());
  };

  const handleClose = () => {
    if (!isSubmitting) {
      onClose();
    }
  };

  const handleQuantityChange = (value: string) => {
    setApprovedQuantity(value);
    setQuantityError('');
    
    // Clear reason error if quantity is back to effective quantity
    const parsedValue = parseFloat(value);
    if (!isNaN(parsedValue) && parsedValue === effectiveQuantity) {
      setReasonError('');
    }
  };

  const parsedQuantity = parseFloat(approvedQuantity);
  const quantityChanged = !isNaN(parsedQuantity) && parsedQuantity !== effectiveQuantity;

  const { dialogRef } = useModalA11y<HTMLDivElement>(isOpen, handleClose);

  if (!isOpen) return null;

  return (
    <div className="fixed inset-0 z-50 overflow-y-auto">
      {/* Backdrop */}
      <div 
        className={`fixed inset-0 bg-black bg-opacity-50 transition-opacity ${
          isSubmitting ? 'cursor-wait' : 'cursor-default'
        }`}
        onClick={handleClose}
      />
      
      {/* Modal */}
      <div className="flex min-h-full items-center justify-center p-4">
        <div
          ref={dialogRef}
          role="dialog"
          aria-modal="true"
          aria-labelledby="quantity-modification-modal-title"
          tabIndex={-1}
          className="relative bg-white dark:bg-secondary-800 rounded-lg shadow-xl max-w-md w-full p-6 focus:outline-none"
        >
          {/* Loading Overlay */}
          {isSubmitting && (
            <div className="absolute inset-0 bg-white dark:bg-secondary-800 bg-opacity-75 rounded-lg flex items-center justify-center z-10">
              <div className="text-center">
                <i className="fa-solid fa-spinner fa-spin text-3xl text-blue-600 dark:text-blue-400 mb-2"></i>
                <p className="text-sm text-gray-600 dark:text-secondary-400">Processing approval...</p>
              </div>
            </div>
          )}
          
          {/* Header */}
          <div className="mb-4">
            <h3 id="quantity-modification-modal-title" className="text-lg font-semibold text-gray-900 dark:text-white">
              Modify Line Item Quantity
            </h3>
            {lineItemName && (
              <p className="mt-1 text-sm text-gray-600 dark:text-secondary-400">
                {lineItemName}
              </p>
            )}
          </div>

          {/* Form */}
          <form onSubmit={handleSubmit}>
            {/* Quantity Context Display */}
            <div className="mb-4 p-3 bg-gray-50 dark:bg-secondary-900 rounded-md space-y-2">
              <div className="flex justify-between items-center">
                <span className="text-sm font-medium text-gray-700 dark:text-secondary-200">Original Requested:</span>
                <span className="text-sm text-gray-900 dark:text-white">
                  {originalRequestedQuantity.toLocaleString()} {unitOfMeasure}
                </span>
              </div>
              {currentApprovedQuantity != null && currentApprovedQuantity !== originalRequestedQuantity && (
                <div className="flex justify-between items-center border-t border-gray-200 dark:border-secondary-700 pt-2">
                  <span className="text-sm font-medium text-blue-700 dark:text-blue-400">Currently Approved:</span>
                  <span className="text-sm font-semibold text-blue-900 dark:text-blue-300">
                    {currentApprovedQuantity.toLocaleString()} {unitOfMeasure}
                  </span>
                </div>
              )}
            </div>

            {/* Approved Quantity Input */}
            <div className="mb-4">
              <label htmlFor="approvedQuantity" className="block text-sm font-medium text-gray-700 dark:text-secondary-200 mb-2">
                Approved Quantity <span className="text-red-600 dark:text-red-400">*</span>
              </label>
              <div className="flex items-center space-x-2">
                <input
                  type="number"
                  id="approvedQuantity"
                  min="1"
                  max={allowIncrease ? undefined : effectiveQuantity}
                  step="any"
                  className={`flex-1 px-3 py-2 border rounded-md bg-white dark:bg-secondary-900 text-gray-900 dark:text-secondary-100 focus:outline-none focus:ring-2 focus:ring-blue-500 ${
                    quantityError ? 'border-red-500' : 'border-gray-300 dark:border-secondary-600'
                  }`}
                  placeholder="Enter quantity"
                  value={approvedQuantity}
                  onChange={(e) => handleQuantityChange(e.target.value)}
                  disabled={isSubmitting}
                  autoFocus
                />
                <span className="text-sm text-gray-600 dark:text-secondary-400 whitespace-nowrap">{unitOfMeasure}</span>
              </div>
              {quantityError && (
                <p className="mt-1 text-sm text-red-600 dark:text-red-400">{quantityError}</p>
              )}
              {!allowIncrease && !quantityError && (
                <p className="mt-1 text-sm text-gray-500 dark:text-secondary-400">
                  <i className="fa-solid fa-circle-info mr-1"></i>
                  This step only allows decreasing the quantity.
                </p>
              )}
              {quantityChanged && !quantityError && (
                <p className="mt-1 text-sm text-blue-600 dark:text-blue-400">
                  <i className="fa-solid fa-info-circle mr-1"></i>
                  Change: {parsedQuantity > effectiveQuantity ? '+' : ''}{(parsedQuantity - effectiveQuantity).toLocaleString()} {unitOfMeasure}
                </p>
              )}
            </div>

            {/* Reason (Required if quantity changed) */}
            <div className="mb-4">
              <label htmlFor="modificationReason" className="block text-sm font-medium text-gray-700 dark:text-secondary-200 mb-2">
                Reason for Modification {quantityChanged && <span className="text-red-600 dark:text-red-400">*</span>}
              </label>
              <textarea
                id="modificationReason"
                rows={3}
                className={`w-full px-3 py-2 border rounded-md bg-white dark:bg-secondary-900 text-gray-900 dark:text-secondary-100 focus:outline-none focus:ring-2 focus:ring-blue-500 ${
                  reasonError ? 'border-red-500' : 'border-gray-300 dark:border-secondary-600'
                }`}
                placeholder={quantityChanged ? "Explain why you're changing the quantity..." : "Optional: Add notes about this approval"}
                value={reason}
                onChange={(e) => {
                  setReason(e.target.value);
                  setReasonError('');
                }}
                disabled={isSubmitting}
              />
              {reasonError && (
                <p className="mt-1 text-sm text-red-600 dark:text-red-400">{reasonError}</p>
              )}
              {quantityChanged && !reasonError && (
                <p className="mt-1 text-sm text-gray-500 dark:text-secondary-400">
                  <i className="fa-solid fa-exclamation-triangle mr-1"></i>
                  Reason required when changing quantity
                </p>
              )}
            </div>

            {/* Actions */}
            <div className="flex justify-end space-x-3">
              <button
                type="button"
                onClick={handleClose}
                className="px-4 py-2 text-sm font-medium text-gray-700 dark:text-secondary-200 bg-white dark:bg-secondary-800 border border-gray-300 dark:border-secondary-600 rounded-md hover:bg-gray-50 dark:hover:bg-secondary-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-blue-500"
                disabled={isSubmitting}
              >
                Cancel
              </button>
              <button
                type="submit"
                className="px-4 py-2 text-sm font-medium text-white bg-green-600 rounded-md hover:bg-green-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-green-500 disabled:opacity-50 disabled:cursor-not-allowed"
                disabled={isSubmitting}
              >
                {isSubmitting ? (
                  <span className="flex items-center">
                    <i className="fa-solid fa-spinner fa-spin mr-2"></i>
                    Approving...
                  </span>
                ) : (
                  quantityChanged ? 'Approve with Changes' : 'Approve'
                )}
              </button>
            </div>
          </form>
        </div>
      </div>
    </div>
  );
}
